import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Flag, ExternalLink, BookOpen, ArrowRight, TrendingUp, TrendingDown, Minus, ArrowLeft } from 'lucide-react';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import WatchlistStarButton from '@/components/WatchlistStarButton';
import ShareToXButton from '@/components/ShareToXButton';
import DividendFilterSegmentedControl from '@/components/DividendFilterSegmentedControl';
import { useAuth } from '@/contexts/AuthContext';
import { useTableControls, SortableTh, TableToolbar, PaginationBar } from '@/components/SortableTableControls';

const FOUNDER_SEARCH_FIELDS = [
  { key: 'ticker', label: 'Ticker', get: (s) => s.ticker },
  { key: 'company', label: 'Company', get: (s) => s.name },
  { key: 'holder', label: 'Holder name', get: (s) => s.top_owner },
];
const FOUNDER_SORT_ACCESSORS = {
  ticker: { get: (s) => s.ticker, type: 'str' },
  company: { get: (s) => s.name, type: 'str' },
  holder: { get: (s) => s.top_owner, type: 'str' },
  float: { get: (s) => s.top_pct, type: 'num' },
  mktcap: { get: (s) => s.market_cap, type: 'num' },
  perf1y: { get: (s) => s.perf_1y, type: 'num' },
  rank: { get: (s) => s.capital_rank ?? s.quality_score, type: 'num' },
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FounderLedPage — public SEO landing surface at /founder-led.
 *
 * Round 98i (Jul 2026) — indexable page targeting "founder-led stocks"
 * / "founder-owned companies" search intent. Built to close the gap
 * where Google + Bing refuse to index our Screener (they see it as
 * too similar to the stock-row page and dedup it out of the index).
 *
 * Design goals:
 *   • Editorial + curated list (not just an embedded screener) — needed
 *     for Google to rank a "commercial-informational" query.
 *   • Server-computed list from /api/pages/founder-led (already filters
 *     out ETFs / trusts / corporate-parent stakes).
 *   • Deep hooks into the app: every row → Stock Modal Insider tab,
 *     "See full Screener" CTA at bottom → /screener with preset preloaded.
 *   • Learn + Glossary links in a Related Reading footer — Glossary
 *     opens in a NEW TAB (per user request) so the founder-led page
 *     doesn't lose the reader.
 */
export default function FounderLedPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tier, setTier] = useState('all');   // 'all' | 'skin' (≥10%) | 'deep' (≥20%) | 'titan' (≥30%)
  // Jul 2026 (Plus paywall) — dividend universe toggle. Default is
  // DIVIDEND (free); Non-Dividend / All pills are locked behind the
  // PlusUpsellModal (admin bypass) via DividendFilterSegmentedControl.
  const [divFilter, setDivFilter] = useState('dividend');
  const [loading, setLoading] = useState(true);

  // SEO — title + description tags. Set on mount so crawlers pick up
  // the founder-led targeting immediately (not the default app title).
  // Round 98m (Jul 2026) — also inject Open Graph + Twitter Card
  // meta tags so JS-capable crawlers (Google, Bing) render rich
  // previews. Non-JS crawlers (Twitterbot, facebookexternalhit,
  // LinkedInBot, Discordbot) get their rich card from the dedicated
  // backend endpoint `/api/share/og/founder-led`, which the
  // page-level Share-to-X button below points at.
  useEffect(() => {
    document.title = 'Founder-Led Stocks — Companies Where Founders Still Own the Float | TwylightCrow';
    const canonical = 'https://twylightcrow.com/founder-led';
    const ogTitle = 'Founder-Led Stocks — Companies Where Founders Still Own the Float';
    const ogDesc = 'Curated from SEC Form 3 filings — public companies where the founder or long-tenured CEO still owns ≥10% of the float. Filter by Skin-in-the-Game, Deep Ownership, or Insider Titan tier. Refreshed nightly.';
    const ogImage = 'https://twylightcrow.com/dividrip-icon.png?v=3';

    const setMetaByName = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    const setMetaByProp = (property, content) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('property', property); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };

    setMetaByName('description',
      'Discover founder-led stocks where the CEO, Chairman, or founding insider still owns ≥10% of the float. Curated from SEC Form 3 filings, refreshed nightly. Filter by ownership tier, quality, and 1-year return.');
    setMetaByName('keywords', 'founder-led stocks, founder owned companies, insider ownership stocks, Form 3 filings, skin in the game investing, founder-CEO stocks, insider float ownership');

    // Open Graph
    setMetaByProp('og:type', 'website');
    setMetaByProp('og:site_name', 'TwylightCrow — DiviDrip');
    setMetaByProp('og:url', canonical);
    setMetaByProp('og:title', ogTitle);
    setMetaByProp('og:description', ogDesc);
    setMetaByProp('og:image', ogImage);
    setMetaByProp('og:image:alt', 'Founder-Led Stocks on TwylightCrow');

    // Twitter / X
    setMetaByName('twitter:card', 'summary');
    setMetaByName('twitter:title', ogTitle);
    setMetaByName('twitter:description', ogDesc);
    setMetaByName('twitter:image', ogImage);
    setMetaByName('twitter:image:alt', 'Founder-Led Stocks on TwylightCrow');

    // Canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);
  }, []);

  // Fetch the pre-computed list. Public endpoint; cookie included so
  // the backend honours the admin-gated non_dividend / all views.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/pages/founder-led`, {
      params: { dividend_filter: divFilter },
      withCredentials: true,
    })
      .then((r) => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [divFilter]);

  // Filter rows by ownership tier — applied client-side since the
  // backend already returns the full top-60 list.
  const filtered = useMemo(() => {
    if (!data?.stocks) return [];
    const rows = data.stocks;
    if (tier === 'skin')  return rows.filter(s => s.top_pct >= 0.10);
    if (tier === 'deep')  return rows.filter(s => s.top_pct >= 0.20);
    if (tier === 'titan') return rows.filter(s => s.top_pct >= 0.30);
    return rows;
  }, [data, tier]);

  // Live per-tier counts for the current universe — makes tier chips
  // visibly respond (page 1 is sorted by top_pct DESC, so its rows
  // barely change when a tier floor is applied; the counts do).
  const tierCounts = useMemo(() => {
    const rows = data?.stocks || [];
    return {
      all: rows.length,
      skin: rows.filter((s) => s.top_pct >= 0.10).length,
      deep: rows.filter((s) => s.top_pct >= 0.20).length,
      titan: rows.filter((s) => s.top_pct >= 0.30).length,
    };
  }, [data]);

  // Jul 2026 — search + column sort + 25-row pagination (client-side,
  // Dashboard-style). Default order = backend's top_pct DESC.
  const ctrl = useTableControls({
    rows: filtered,
    searchFields: FOUNDER_SEARCH_FIELDS,
    sortAccessors: FOUNDER_SORT_ACCESSORS,
    pageSize: 25,
  });

  const fmt$ = (n) => n == null ? '—' : n >= 1e9 ? `$${(n/1e9).toFixed(1)}B`
                                     : n >= 1e6 ? `$${(n/1e6).toFixed(0)}M`
                                     : `$${n.toFixed(0)}`;
  const fmtPct = (n) => n == null ? '—' : `${(n * 100).toFixed(1)}%`;
  const fmtSigned = (n) => n == null ? '—' : `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
  const verdictColor = (v) =>
    v === 'BUY'   ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10' :
    v === 'WATCH' ? 'text-amber-700 dark:text-amber-300 bg-amber-500/10' :
    v === 'AVOID' ? 'text-rose-700 dark:text-rose-300 bg-rose-500/10' :
                    'text-muted-foreground bg-muted/40';


  return (
    <div className="min-h-screen bg-background">
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 pb-0" data-testid="founder-led-page">
        {/* Back navigation */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"
          data-testid="founder-led-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {/* Hero */}
        <header className="mb-10">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-700 dark:text-sky-300 text-[10px] font-bold uppercase tracking-widest mb-3">
              <Flag className="w-3 h-3" /> SEC Form 3 · Skin in the Game
            </div>
            {/* Round 98m — page-level Share-to-X. Uses backend OG
                endpoint URL so social unfurlers (which don't run JS)
                render a rich card with the founder-led title +
                description instead of the generic site card. */}
            <ShareToXButton
              variant="button"
              url="https://twylightcrow.com/api/share/og-page/founder-led"
              text="Founder-Led Stocks — public companies where the founder still owns ≥10% of the float, curated from SEC Form 3 filings. Skin-in-the-game investing:"
              hashtags={['FounderLed', 'DividendInvesting', 'SEC']}
              ticker="founder-led"
            />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground leading-[1.05]">
            Founder-Led Stocks
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            {loading
              ? 'Loading founder-led universe…'
              : (
                <>
                  <span className="font-semibold text-foreground">{data?.with_material_stake?.toLocaleString() ?? '—'}</span>{' '}
                  companies where a top insider owns <strong>≥ 10% of the float</strong>, curated from{' '}
                  <span className="font-semibold text-foreground">{data?.total_founder_detected?.toLocaleString() ?? '—'}</span>{' '}
                  founder-detected tickers via SEC Form 3 filings. Refreshed nightly.
                </>
              )
            }
          </p>
        </header>

        {/* Editorial body — SEO fuel */}
        <section className="prose prose-slate dark:prose-invert max-w-none mb-10 text-[15px] leading-relaxed">
          <h2 className="text-xl font-bold mb-3">Why founder-led ownership matters</h2>
          <p className="text-muted-foreground">
            When a founder or long-tenured CEO still owns a material percentage of the shares outstanding, incentives
            line up in a way that Wall Street salaries can&apos;t replicate. The founder eats their own cooking —
            share dilution costs them personally, dividend cuts cost them personally, and a bad acquisition costs
            them personally. Academic research going back to Fahlenbrach (2009) shows founder-CEO firms
            outperformed the S&amp;P 500 by ~4.4% annualized over multi-decade windows.
          </p>
          <p className="text-muted-foreground mt-3">
            The trick is separating <strong>real</strong> founder-led compounders from three failure modes: (1) parent-
            owned subsidiaries that <em>look</em> founder-led on paper but are just controlled by another public
            company, (2) founder-controlled biotechs with no revenue where the &ldquo;skin in the game&rdquo; is imaginary
            because the shares have no exit market, and (3) dual-class shareholder power grabs where the founder
            has 100% voting rights but only 5% economic exposure. This page filters out (1) via corporate-parent
            regex on the Form 3 top-holder, requires <strong>positive fundamentals</strong> (Rule of 40 FCF, Altman Z
            Safe zone) to escape (2), and surfaces raw float % — not voting % — to expose (3).
          </p>
          <p className="text-muted-foreground mt-3">
            Famous examples our filter would have caught early: Jeff Bezos at Amazon (1997 IPO, ~42% of float),
            Elon Musk at Tesla (2010, ~28%), Mark Zuckerberg at Meta (2012, ~28% economic + supervoting Class B),
            Jensen Huang at NVIDIA (still 3.5% today of a $3T company — the largest personal shareholding on the
            planet). The pattern isn&apos;t &ldquo;founder owns some&rdquo; — it&apos;s &ldquo;founder owns enough that a bad decision
            costs them tens of millions of their own money.&rdquo;
          </p>
        </section>

        {/* Jul 2026 (Plus paywall) — universe toggle. Dividend is the
            free default; Non-Dividend / All open the Plus upsell for
            free users (admins switch normally). */}
        <div className="mb-4" data-testid="founder-led-universe-toggle">
          <DividendFilterSegmentedControl
            value={divFilter}
            onChange={setDivFilter}
            isAdmin={user?.role === 'admin'}
            instanceId="founder-led"
          />
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="founder-led-filter-chips">
          {[
            { key: 'all',   label: `All (${tierCounts.all})` },
            { key: 'skin',  label: `Skin in the Game (≥ 10%) · ${tierCounts.skin}` },
            { key: 'deep',  label: `Deep Ownership (≥ 20%) · ${tierCounts.deep}` },
            { key: 'titan', label: `Insider Titan (≥ 30%) · ${tierCounts.titan}` },
          ].map(t => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTier(t.key)}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-all ${
                tier === t.key
                  ? 'bg-sky-600 text-white border-sky-600'
                  : 'bg-background text-foreground border-border hover:border-sky-500/40 hover:bg-sky-500/5'
              }`}
              data-testid={`founder-led-tier-${t.key}`}
            >{t.label}</button>
          ))}
        </div>

        {/* Round 98j (Jul 2026) — Freshly Detected This Week sidebar.
            Compact horizontal chip strip above the main table on
            mobile, a real sidebar on desktop (grid). Sources
            data.freshly_detected — tickers where form3_founder_stake
            flipped to detected in the last 7 days. Doubles as social-
            share content ("This week's new founder-led names") and
            gives Google a signal that the page updates weekly. */}
        {data?.freshly_detected?.length > 0 && (
          <div className="mb-6 rounded-lg border border-sky-500/25 bg-gradient-to-br from-sky-500/5 via-transparent to-transparent p-4" data-testid="fresh-detected-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-sky-700 dark:text-sky-300">
                  <Flag className="w-3 h-3" /> Freshly Detected This Week
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Tickers where a founder-scale insider position was newly detected in the last 7 days.
                </p>
              </div>
              <span className="text-[10px] font-mono text-muted-foreground shrink-0">{data.freshly_detected.length} new</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {data.freshly_detected.map((f) => (
                <div
                  key={f.ticker}
                  className="relative rounded-md border border-border bg-background hover:border-sky-500/50 hover:bg-sky-500/5 p-2.5 transition-all cursor-pointer"
                  onClick={() => navigate(`/stock/${f.ticker}?tab=insider-activity`)}
                  data-testid={`fresh-detected-${f.ticker}`}
                >
                  {/* Round 98l — Watchlist star in the top-right corner
                      of every freshly-detected card. onClick handler
                      on inner wrapper stops the outer navigate. */}
                  <div className="absolute top-1 right-1" onClick={(e) => e.stopPropagation()}>
                    <WatchlistStarButton ticker={f.ticker} size="w-3.5 h-3.5" />
                  </div>
                  <div className="flex items-baseline justify-between gap-2 pr-6">
                    <span className="font-bold text-sky-700 dark:text-sky-300 text-sm">{f.ticker}</span>
                    <span className="font-mono text-xs font-bold">{(f.top_pct * 100).toFixed(1)}%</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate mt-0.5" title={f.top_owner}>
                    {f.top_owner || '—'}
                  </div>
                  {f.perf_1y != null && (
                    <div className={`text-[10px] font-mono mt-0.5 ${f.perf_1y >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                      1Y {f.perf_1y >= 0 ? '+' : ''}{f.perf_1y.toFixed(1)}%
                    </div>
                  )}
                  {/* Jul 2026 — Share to X, bottom-right. Pre-filled with
                      ticker, founder name and stake %; stopPropagation so
                      the tile's navigate doesn't fire. */}
                  <div className="flex justify-end mt-1" onClick={(e) => e.stopPropagation()}>
                    <ShareToXButton
                      variant="button"
                      label="Share to X"
                      size="w-3 h-3"
                      ticker={f.ticker}
                      url="https://twylightcrow.com/api/share/og-page/founder-led"
                      text={`Founder alert: ${f.top_owner || 'a founder-scale insider'} holds ${(f.top_pct * 100).toFixed(1)}% of $${f.ticker} — freshly detected from SEC Form 3 filings on the Founder-Led radar:`}
                      hashtags={['FounderLed', 'SEC']}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Table — searchable (Ticker / Company / Holder), sortable
            headers with direction chevrons, 25 rows per page. */}
        <TableToolbar
          ctrl={ctrl}
          searchFields={FOUNDER_SEARCH_FIELDS}
          placeholder="Search founder-led stocks…"
          testIdPrefix="founder-led"
        />
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="founder-led-table">
              <thead className="bg-muted/40 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="w-8 px-2 py-2" aria-label="Watchlist"></th>
                  <SortableTh ctrl={ctrl} sortKey="ticker" label="Ticker" testIdPrefix="founder-led" />
                  <SortableTh ctrl={ctrl} sortKey="company" label="Company" testIdPrefix="founder-led" />
                  <SortableTh ctrl={ctrl} sortKey="holder" label="Top Holder" testIdPrefix="founder-led" />
                  <SortableTh ctrl={ctrl} sortKey="float" label="% of Float" align="right" testIdPrefix="founder-led" />
                  <SortableTh ctrl={ctrl} sortKey="mktcap" label="Mkt Cap" align="right" testIdPrefix="founder-led" />
                  <SortableTh ctrl={ctrl} sortKey="perf1y" label="1Y Return" align="right" testIdPrefix="founder-led" />
                  <SortableTh ctrl={ctrl} sortKey="rank" label="Rank" align="center" testIdPrefix="founder-led" />
                  <th className="w-10 px-2 py-2" aria-label="Share"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading && (
                  <tr><td colSpan={9} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
                )}
                {!loading && ctrl.pageRows.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-6 text-muted-foreground">No matches — try loosening the tier filter or clearing the search.</td></tr>
                )}
                {ctrl.pageRows.map((s) => (
                  <tr key={s.ticker}
                      className="hover:bg-muted/30 cursor-pointer"
                      onClick={() => navigate(`/stock/${s.ticker}?tab=insider-activity`)}
                      data-testid={`founder-led-row-${s.ticker}`}>
                    <td className="px-2 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                      <WatchlistStarButton ticker={s.ticker} />
                    </td>
                    <td className="px-3 py-2.5 font-bold text-sky-700 dark:text-sky-300">{s.ticker}</td>
                    <td className="px-3 py-2.5">
                      <div className="max-w-[280px] truncate" title={s.name}>{s.name}</div>
                      {s.sector && <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{s.sector}</div>}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="max-w-[220px] truncate" title={s.top_owner}>{s.top_owner || '—'}</div>
                      {s.top_role && <div className="text-[10px] text-muted-foreground">{s.top_role}</div>}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold">{fmtPct(s.top_pct)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-muted-foreground">{fmt$(s.market_cap)}</td>
                    <td className={`px-3 py-2.5 text-right font-mono ${s.perf_1y == null ? 'text-muted-foreground' : s.perf_1y >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                      {fmtSigned(s.perf_1y)}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {s.capital_verdict ? (
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${verdictColor(s.capital_verdict)}`}>
                          {s.capital_verdict === 'BUY' ? <TrendingUp className="w-3 h-3" />
                            : s.capital_verdict === 'AVOID' ? <TrendingDown className="w-3 h-3" />
                            : <Minus className="w-3 h-3" />}
                          {s.capital_rank ?? '—'} {s.capital_verdict}
                        </span>
                      ) : s.quality_score != null ? (
                        /* Dividend rows: Capital Rank is a non-div metric,
                           show the Dividend Quality Score pill instead. */
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase text-sky-700 dark:text-sky-300 bg-sky-500/10"
                          title={`Dividend Quality Score ${s.quality_score}/100`}
                          data-testid={`founder-led-quality-${s.ticker}`}
                        >
                          Q {s.quality_score}
                        </span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-2 py-2.5 text-center">
                      <ShareToXButton
                        ticker={s.ticker}
                        text={`$${s.ticker} — founder-led: ${s.top_owner || 'top holder'} owns ${((s.top_pct || 0) * 100).toFixed(1)}% of the float (via SEC Form 3). Skin-in-the-game investing:`}
                        hashtags={['FounderLed', 'DividendInvesting', 'SEC']}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="mb-10">
          <PaginationBar ctrl={ctrl} testIdPrefix="founder-led" />
        </div>

        {/* Screener CTA */}
        <div className="border border-sky-500/30 rounded-lg p-5 mb-10 bg-sky-500/5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-bold mb-1">Compose your own founder-led screen</h3>
              <p className="text-sm text-muted-foreground">Add sector, revenue growth, Rule of 40, or trailing return filters on top of the founder-led base preset.</p>
            </div>
            <Link
              to="/screener?filter=non_dividend&preset=founder_led_full"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-sky-600 text-white text-sm font-semibold hover:bg-sky-500 transition-colors shrink-0"
              data-testid="founder-led-screener-cta"
            >
              Open Screener with preset <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Related Reading — Learn guides + Glossary anchors */}
        <section className="border-t border-border pt-8">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5" /> Related reading
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Learn guides — open in same tab (in-app content) */}
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Learn guides</div>
              <ul className="space-y-2 text-sm">
                <li><Link to="/learn/founders-vision-execution" className="text-sky-700 dark:text-sky-300 hover:underline" data-testid="learn-founders-vision">Founder&apos;s Vision — Why Insider Ownership Beats Hired Guns</Link></li>
                <li><Link to="/learn/inside-the-growth-engine" className="text-sky-700 dark:text-sky-300 hover:underline" data-testid="learn-inside-growth">Inside the Growth Engine — How Founder-CEOs Compound Capital</Link></li>
                <li><Link to="/learn/momentum-trading-non-dividend" className="text-sky-700 dark:text-sky-300 hover:underline" data-testid="learn-momentum">Momentum Trading Non-Dividend Stocks</Link></li>
                <li><Link to="/learn/free-cash-flow-growth" className="text-sky-700 dark:text-sky-300 hover:underline" data-testid="learn-fcf-growth">Free Cash Flow Growth — the Founder-CEO Weapon</Link></li>
                <li><Link to="/learn/value-traps-of-growth-collapses" className="text-sky-700 dark:text-sky-300 hover:underline" data-testid="learn-value-traps">When Founder-Led Fails — Growth Collapses &amp; Value Traps</Link></li>
              </ul>
            </div>
            {/* Glossary — open in NEW TAB (per user request) so the founder-led page stays open */}
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Glossary terms</div>
              <ul className="space-y-2 text-sm">
                <li><a href="/glossary#entry-capital-analytics-composite-score" target="divdrip-glossary" className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-300 hover:underline" data-testid="glossary-capital-analytics">Capital Analytics Composite Score <ExternalLink className="w-3 h-3" /></a></li>
                <li><a href="/glossary#entry-rule-of-40" target="divdrip-glossary" className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-300 hover:underline" data-testid="glossary-rule-40">Rule of 40 <ExternalLink className="w-3 h-3" /></a></li>
                <li><a href="/glossary#entry-altman-z-score" target="divdrip-glossary" className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-300 hover:underline" data-testid="glossary-altman">Altman Z-Score <ExternalLink className="w-3 h-3" /></a></li>
                <li><a href="/glossary#entry-beneish-m-score" target="divdrip-glossary" className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-300 hover:underline" data-testid="glossary-beneish">Beneish M-Score <ExternalLink className="w-3 h-3" /></a></li>
              </ul>
            </div>
          </div>
          {data?.computed_at && (
            <p className="text-[10px] text-muted-foreground uppercase tracking-widest mt-6">
              Data refreshed: {new Date(data.computed_at).toLocaleString()}
            </p>
          )}
        </section>
      </main>
      <SiteDisclaimer />
    </div>
  );
}
